// @ts-check
/**
 * Shipping carrier registry + shipments.
 *
 * Phase A is manual AWB entry. When the Shiprocket / ShipGlobal API integration lands,
 * createShipment keeps the same signature — the backend just stops needing an AWB from
 * the admin and gets one from the carrier instead.
 */

import api from '../utils/apiClient';

/** Parcel values typed on the order page (dead weight kg, L/B/H cm); blank ones are left to the products. */
const appendPackage = (params, pkg) => {
  for (const key of ['weight_kg', 'length_cm', 'breadth_cm', 'height_cm']) {
    const value = Number(pkg?.[key]);
    if (value > 0) params.append(key, String(value));
  }
};

const adminShippingService = {
  // ── Carrier registry ──────────────────────────────────────────────────────

  /**
   * @param {boolean} [activeOnly] Only carriers available for new shipments
   * @returns {Promise<Object[]>}
   */
  async getCarriers(activeOnly = false) {
    return api.get(`/api/admin/shipping-carriers${activeOnly ? '?active_only=true' : ''}`);
  },

  /**
   * @param {Object} carrierData
   * @returns {Promise<Object>}
   */
  async createCarrier(carrierData) {
    return api.post('/api/admin/shipping-carriers', carrierData);
  },

  /**
   * @param {string} carrierId
   * @param {Object} carrierData
   * @returns {Promise<Object>}
   */
  async updateCarrier(carrierId, carrierData) {
    return api.patch(`/api/admin/shipping-carriers/${carrierId}`, carrierData);
  },

  /**
   * Deletes the carrier, or deactivates it if past shipments reference it.
   * @param {string} carrierId
   * @returns {Promise<{ message: string, deactivated: boolean }>}
   */
  async deleteCarrier(carrierId) {
    return api.del(`/api/admin/shipping-carriers/${carrierId}`);
  },

  /**
   * Verifies the carrier's API credentials without creating anything. Always resolves —
   * a failure comes back as { connected: false, reason } rather than throwing.
   * @param {string} carrierId
   * @returns {Promise<{ connected: boolean, reason: string | null }>}
   */
  async testConnection(carrierId) {
    return api.post(`/api/admin/shipping-carriers/${carrierId}/test-connection`, {});
  },

  /**
   * Pickup addresses registered with the carrier. `name` is the nickname the carrier's
   * order API expects — it must match exactly, hence picking rather than typing.
   * @param {string} carrierId
   * @returns {Promise<{ locations: Object[], reason: string | null }>}
   */
  async getPickupLocations(carrierId) {
    return api.get(`/api/admin/shipping-carriers/${carrierId}/pickup-locations`);
  },

  /**
   * @param {string} orderId
   * @param {string} carrierId
   * @param {boolean} [sendEmail]
   * @param {{ csb5Status?: number, service?: string, courierId?: string }} [options]
   * @returns {Promise<Object>}
   */
  async createShipmentViaCarrier(orderId, carrierId, sendEmail = true, options = {}) {
    const params = new URLSearchParams({
      carrier_id: carrierId,
      send_email: String(sendEmail),
    });
    if (options.csb5Status !== undefined) {
      params.append('csb5_status', String(options.csb5Status));
    }
    if (options.service) {
      params.append('service', options.service);
    }
    if (options.courierId) {
      params.append('courier_id', options.courierId);
    }
    appendPackage(params, options.package);
    if (Number(options.declaredValue) > 0) params.append('declared_value', String(Number(options.declaredValue)));
    return api.post(`/api/admin/orders/${orderId}/shipments/via-carrier?${params}`, {});
  },

  /**
   * ShipGlobal service + price options for this order's lane. Also doubles as a
   * serviceability check for the destination postcode.
   * @param {string} orderId
   * @param {string} carrierId
   * @returns {Promise<{ title: string, notes: string, transit_time: string, price: number, currency: string }[]>}
   */
  async getShipGlobalRates(orderId, carrierId) {
    const params = new URLSearchParams({ carrier_id: carrierId });
    return api.post(`/api/admin/orders/${orderId}/shipments/shipglobal-rates?${params}`, {});
  },

  /**
   * Shiprocket courier + rate options for this order's lane.
   * @param {string} orderId
   * @param {string} carrierId
   * @param {{weight_kg?: string, length_cm?: string, breadth_cm?: string, height_cm?: string}} [pkg] typed parcel overrides
   * @returns {Promise<{ couriers: { courier_id: string, courier_name: string, rate: number, etd: string, estimated_delivery_days: string, rating: string }[], package: Record<string, number>, cod: boolean, declared_value: number, declared_value_editable: boolean }>}
   */
  async getShiprocketRates(orderId, carrierId, pkg, declaredValue) {
    const params = new URLSearchParams({ carrier_id: carrierId });
    appendPackage(params, pkg);
    if (Number(declaredValue) > 0) params.append('declared_value', String(Number(declaredValue)));
    return api.post(`/api/admin/orders/${orderId}/shipments/shiprocket-rates?${params}`, {});
  },

  /**
   * The explicit, separate "Pay & Get Label" step — charges the carrier's wallet.
   * Never call this speculatively; it's a deliberate, costed admin action.
   * @param {string} shipmentId
   * @returns {Promise<Object>}
   */
  async generateLabel(shipmentId) {
    return api.post(`/api/admin/shipments/${shipmentId}/generate-label`, {});
  },

  /**
   * Re-read the cost Shiprocket currently bills for a shipment booked through it.
   * @param {string} shipmentId
   * @returns {Promise<Object>}
   */
  async refreshCharges(shipmentId) {
    return api.post(`/api/admin/shipments/${shipmentId}/refresh-charges`, {});
  },

  // ── Shipments ─────────────────────────────────────────────────────────────

  /**
   * @param {string} orderId
   * @returns {Promise<Object[]>}
   */
  async getShipments(orderId) {
    return api.get(`/api/admin/orders/${orderId}/shipments`);
  },

  /**
   * Every shipment across every order, newest first — for the standalone Shipments
   * tracking page, not the per-order panel. `totals` are real sums over the entire
   * filtered set (not just the current page) — that's the actual spend-tracking figure.
   * @param {Object} [params]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @param {string} [params.search] Matches order number or AWB
   * @param {string} [params.status]
   * @param {string} [params.order_type] 'gift' | 'sample' | 'standard'
   * @returns {Promise<{items: Object[], total: number, page: number, limit: number, pages: number, totals: {shipping_charge: number, declared_value: number}}>}
   */
  async listAllShipments(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.order_type) queryParams.append('order_type', params.order_type);
    return api.get(`/api/admin/shipments?${queryParams}`);
  },

  /**
   * Records a dispatched parcel. Moves the order to Shipped and — unless send_email is
   * false — emails the customer their tracking link.
   * @param {string} orderId
   * @param {Object} shipmentData
   * @returns {Promise<Object>}
   */
  async createShipment(orderId, shipmentData) {
    return api.post(`/api/admin/orders/${orderId}/shipments`, shipmentData);
  },

  /**
   * @param {string} shipmentId
   * @param {Object} shipmentData
   * @returns {Promise<Object>}
   */
  async updateShipment(shipmentId, shipmentData) {
    return api.patch(`/api/admin/shipments/${shipmentId}`, shipmentData);
  },

  /**
   * @param {string} shipmentId
   * @returns {Promise<{ message: string }>}
   */
  async resendTrackingEmail(shipmentId) {
    return api.post(`/api/admin/shipments/${shipmentId}/resend-email`, {});
  },

  /**
   * @param {string} shipmentId
   * @returns {Promise<{ message: string }>}
   */
  async cancelShipment(shipmentId) {
    return api.post(`/api/admin/shipments/${shipmentId}/cancel`, {});
  },

  /**
   * @param {string} shipmentId
   * @returns {Promise<{ message: string }>}
   */
  async deleteShipment(shipmentId) {
    return api.del(`/api/admin/shipments/${shipmentId}`);
  },
};

export default adminShippingService;
